import sanitizeFilename from 'sanitize-filename';
import allowedFileTypes from '../../shared/allowed-file-types.json';

export interface FileValidationOptions {
  filename?: string;
  mimeType?: string;
  fileExtension?: string;
  file?: File;
}

export const getFileExtension = (filename: string): string => {
  const ext = filename.includes('.')
    ? filename.split('.').pop()?.toLowerCase()
    : '';
  return ext ? `.${ext}` : '';
};

export const formatValidationError = (failedFiles: string[]): string => {
  if (failedFiles.length === 0) return '';

  if (failedFiles.length === 1) {
    return `"${failedFiles[0]}" is not a supported format.`;
  }

  return `The following files have unsupported file types:\n${failedFiles.join(
    ', ',
  )}`;
};

const acceptableMimeTypesSet = new Set(
  allowedFileTypes.mimeTypes.map((m) => m.toLowerCase()),
);
const allowedExtensionsSet = new Set(
  allowedFileTypes.extensions.map((ext) => ext.toLowerCase()),
);
const blockedInnerSegmentsSet = new Set(
  (allowedFileTypes.blockedInnerFilenameSegments || []).map((seg: string) =>
    seg.toLowerCase(),
  ),
);

const mimeByExtensionLookup: Readonly<Record<string, string>> =
  allowedFileTypes.mimeByExtension;

const PRIMARY_MIME_FALLBACK = 'application/pdf';

export function guessPrimaryMimeTypeForAllowedFilename(
  fileName: string,
): string {
  const ext = fileName.includes('.')
    ? (fileName.split('.').pop()?.toLowerCase() ?? '')
    : '';
  if (ext === '') {
    return PRIMARY_MIME_FALLBACK;
  }
  const mapped = mimeByExtensionLookup[ext];
  if (mapped !== undefined && isAcceptableMimeType(mapped)) {
    return mapped;
  }
  return PRIMARY_MIME_FALLBACK;
}

export const isAcceptableMimeType = (mimeType: string): boolean => {
  if (!mimeType || typeof mimeType !== 'string') return false;
  return acceptableMimeTypesSet.has(mimeType.trim().toLowerCase());
};

export const isAllowedExtension = (extension: string): boolean => {
  if (!extension || typeof extension !== 'string') return false;
  const normalized = extension.startsWith('.') ? extension : `.${extension}`;
  return allowedExtensionsSet.has(normalized.trim().toLowerCase());
};

const INVISIBLE_CHARS_RE =
  /[\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff\u00ad\u061c]/g;

const checkAllExtensionsAllowed = (filename: string): void => {
  if (!filename || typeof filename !== 'string') return;

  const dotIndex = filename.indexOf('.');
  if (dotIndex === -1) return;

  const segments = filename.slice(dotIndex + 1).split('.');
  if (segments.length <= 1) return;

  const lastIndex = segments.length - 1;

  for (let i = 0; i < lastIndex; i++) {
    const normalized = segments[i]
      .toLowerCase()
      .trim()
      .replace(INVISIBLE_CHARS_RE, '');
    if (!normalized || normalized.includes('/') || normalized.includes('\\')) {
      continue;
    }
    if (blockedInnerSegmentsSet.has(normalized)) {
      throw new Error(
        `"${filename}" contains a blocked file extension segment ".${segments[i]}" in the middle of the filename.`,
      );
    }
  }

  const lastExt = segments[lastIndex]
    .toLowerCase()
    .trim()
    .replace(INVISIBLE_CHARS_RE, '');
  if (
    lastExt &&
    !lastExt.includes('/') &&
    !lastExt.includes('\\') &&
    !isAllowedExtension(`.${lastExt}`)
  ) {
    throw new Error(`"${filename}" is not a supported format.`);
  }
};

export const validateFileUpload = (options: FileValidationOptions): string => {
  const { filename, mimeType, fileExtension, file } = options;

  const actualMimeType = file?.type || mimeType;
  const actualFilename = file?.name || filename;

  if (actualMimeType && !isAcceptableMimeType(actualMimeType)) {
    throw new Error(`Unsupported file type`);
  }

  if (fileExtension && !isAllowedExtension(fileExtension)) {
    throw new Error(`Unsupported file type`);
  }

  if (!actualFilename || actualFilename.trim() === '') {
    return '';
  }

  checkAllExtensionsAllowed(actualFilename);

  const sanitizedFilename = sanitizeFilename(actualFilename.normalize('NFC'), {
    replacement: '_',
  }).substring(0, 255);
  const extension = sanitizedFilename.split('.').pop()?.toLowerCase();

  if (!extension) {
    throw new Error(`Unsupported file type`);
  }

  if (!isAllowedExtension(extension)) {
    throw new Error(`Unsupported file type`);
  }

  if (fileExtension && actualFilename) {
    const normalizedFileExtension = fileExtension.startsWith('.')
      ? fileExtension.slice(1)
      : fileExtension;
    if (extension !== normalizedFileExtension.toLowerCase()) {
      throw new Error(`Unsupported file type`);
    }
  }

  return sanitizedFilename;
};
